import React, { useEffect, useState, useCallback } from "react";
import {
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Loader,
  ExternalLink,
} from "lucide-react";
import { toast } from "react-toastify";
import { reclamationAPI } from "../../services/api";
import { useSocketEvent, useSocketResync } from "../../hooks/useSocketEvent";
import { withVersion } from "../../utils/concurrency";
import type { Reclamation, ReclamationStatus } from "../../types/reclamation";

interface Props {
  shipmentId: string;
}

/**
 * `shipmentId` arrives populated ({_id, shipmentNumber, status}) from the list
 * endpoint but as a bare id over the socket, so never compare it directly.
 */
const reclamationShipmentId = (r: Reclamation): string =>
  typeof r.shipmentId === "string" ? r.shipmentId : r.shipmentId?._id;

const STATUS_STYLES: Record<ReclamationStatus, string> = {
  open: "bg-blue-100 text-blue-700",
  under_review: "bg-amber-100 text-amber-700",
  approved: "bg-green-100 text-green-700",
  rejected: "bg-red-100 text-red-700",
  settled: "bg-gray-100 text-gray-700",
};

const STATUS_LABELS: Record<ReclamationStatus, string> = {
  open: "Open",
  under_review: "Under Review",
  approved: "Approved",
  rejected: "Rejected",
  settled: "Settled",
};

const TYPE_LABELS: Record<string, string> = {
  damage: "Damage",
  loss: "Loss",
  shortage: "Shortage",
  delay: "Delay",
  documentation: "Documentation",
  other: "Other",
};

const NEXT_STATUSES: Record<ReclamationStatus, ReclamationStatus[]> = {
  open: ["under_review", "rejected"],
  under_review: ["approved", "rejected"],
  approved: ["settled"],
  rejected: [],
  settled: [],
};

const ReclamationSection: React.FC<Props> = ({ shipmentId }) => {
  const [reclamations, setReclamations] = useState<Reclamation[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [updating, setUpdating] = useState<string | null>(null);

  // Per-reclamation resolution form state
  const [resolutionInputs, setResolutionInputs] = useState<
    Record<string, { approvedAmount: string; resolutionNote: string }>
  >({});

  const load = useCallback(async () => {
    try {
      const res = await reclamationAPI.getReclamations({ shipmentId });
      setReclamations(res.data.data.reclamations ?? []);
    } catch {
      // silently fail — section just stays empty
    } finally {
      setLoading(false);
    }
  }, [shipmentId]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Merge a pushed reclamation into local state, keeping the newest-first order
   * `load()` establishes. Idempotent by _id: the dispatcher who made the change
   * also receives their own broadcast (they are in the company room), and a
   * reconnect can redeliver.
   */
  const mergeReclamation = useCallback(
    (incoming: Reclamation) => {
      if (reclamationShipmentId(incoming) !== shipmentId) return;

      setReclamations((prev) => {
        const idx = prev.findIndex((r) => r._id === incoming._id);
        if (idx === -1) return [incoming, ...prev];

        const next = [...prev];
        next[idx] = incoming;
        return next;
      });
    },
    [shipmentId],
  );

  // A customer or driver filing a claim, and — the case that previously had no
  // in-app signal whatsoever — another dispatcher resolving one.
  useSocketEvent<Reclamation>("reclamation:created", mergeReclamation);
  useSocketEvent<Reclamation>("reclamation:updated", mergeReclamation);

  // Nothing is replayed across a dropped connection.
  useSocketResync(load);

  const handleStatusUpdate = async (
    id: string,
    status: ReclamationStatus,
  ) => {
    const inputs = resolutionInputs[id] ?? { approvedAmount: "", resolutionNote: "" };
    setUpdating(id);
    try {
      const res = await reclamationAPI.updateStatus(
        id,
        withVersion(
          {
            status,
            approvedAmount: inputs.approvedAmount
              ? Number(inputs.approvedAmount)
              : undefined,
            resolutionNote: inputs.resolutionNote || undefined,
          },
          reclamations.find((r) => r._id === id),
        ),
      );
      setReclamations((prev) =>
        prev.map((r) => (r._id === id ? res.data.data.reclamation : r)),
      );
      toast.success(`Reclamation marked as ${STATUS_LABELS[status]}`);
    } catch (err: any) {
      toast.error(err.response?.data?.message ?? "Failed to update status");
    } finally {
      setUpdating(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center space-x-2 py-4 text-gray-400">
        <Loader className="h-4 w-4 animate-spin" />
        <span className="text-sm">Loading reclamations…</span>
      </div>
    );
  }

  if (reclamations.length === 0) {
    return (
      <div className="text-center py-8 text-gray-400">
        <AlertTriangle className="h-8 w-8 mx-auto mb-2 opacity-40" />
        <p className="text-sm">No reclamations filed for this shipment</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {reclamations.map((rec) => {
        const isExpanded = expandedId === rec._id;
        const inputs = resolutionInputs[rec._id] ?? { approvedAmount: "", resolutionNote: "" };
        const nextStatuses = NEXT_STATUSES[rec.status] ?? [];

        return (
          <div key={rec._id} className="border border-gray-200 rounded-xl overflow-hidden">
            {/* Row header */}
            <button
              type="button"
              onClick={() => setExpandedId(isExpanded ? null : rec._id)}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors text-left"
            >
              <div className="flex items-center space-x-3 flex-wrap gap-y-1">
                <span className="font-mono text-sm font-semibold text-gray-700">
                  {rec.reclamationNumber}
                </span>
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_STYLES[rec.status]}`}>
                  {STATUS_LABELS[rec.status]}
                </span>
                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                  {TYPE_LABELS[rec.type]}
                </span>
                {rec.source === "driver" ? (
                  <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                    Driver
                  </span>
                ) : (
                  <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
                    Customer
                  </span>
                )}
              </div>
              <div className="flex items-center space-x-3">
                {rec.claimedAmount != null && (
                  <span className="text-sm font-medium text-gray-600">
                    {rec.currency} {rec.claimedAmount.toLocaleString()}
                  </span>
                )}
                <span className="text-xs text-gray-400">
                  {new Date(rec.createdAt).toLocaleDateString(undefined, {
                    day: "numeric", month: "short", year: "numeric",
                  })}
                </span>
                {isExpanded ? (
                  <ChevronUp className="h-4 w-4 text-gray-400" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                )}
              </div>
            </button>

            {/* Expanded details */}
            {isExpanded && (
              <div className="px-4 pb-4 space-y-4 border-t border-gray-100 pt-3">
                {/* Claimant */}
                <div className="space-y-2 text-sm">
                  <div>
                    <p className="text-xs text-gray-400">Email</p>
                    <p className="font-medium text-gray-800 break-all">{rec.claimantEmail}</p>
                  </div>
                  {(rec.claimantName || rec.claimantPhone) && (
                    <div className="grid grid-cols-2 gap-3">
                      {rec.claimantName && (
                        <div>
                          <p className="text-xs text-gray-400">Name</p>
                          <p className="font-medium text-gray-800 truncate">{rec.claimantName}</p>
                        </div>
                      )}
                      {rec.claimantPhone && (
                        <div>
                          <p className="text-xs text-gray-400">Phone</p>
                          <p className="font-medium text-gray-800">{rec.claimantPhone}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Incident details */}
                {(rec.incidentDate || rec.incidentTime || rec.incidentStage) && (
                  <div className="bg-amber-50 border border-amber-100 rounded-lg px-3 py-2.5 space-y-1.5">
                    <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide">Incident Details</p>
                    <div className="grid grid-cols-3 gap-3 text-sm">
                      {rec.incidentDate && (
                        <div>
                          <p className="text-xs text-gray-400">Date</p>
                          <p className="font-medium text-gray-800">
                            {new Date(rec.incidentDate).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}
                          </p>
                        </div>
                      )}
                      {rec.incidentTime && (
                        <div>
                          <p className="text-xs text-gray-400">Time</p>
                          <p className="font-medium text-gray-800">{rec.incidentTime}</p>
                        </div>
                      )}
                      {rec.incidentStage && (
                        <div>
                          <p className="text-xs text-gray-400">Stage</p>
                          <p className="font-medium text-gray-800 capitalize">
                            {rec.incidentStage === "in_transit" ? "In Transit" : rec.incidentStage.charAt(0).toUpperCase() + rec.incidentStage.slice(1)}
                          </p>
                          {rec.incidentStage === "other" && rec.incidentStageNote && (
                            <p className="text-xs text-gray-500 mt-0.5">{rec.incidentStageNote}</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Description */}
                <div>
                  <p className="text-xs text-gray-400 mb-1">Description</p>
                  <p className="text-sm text-gray-800 whitespace-pre-wrap bg-gray-50 rounded-lg p-3">
                    {rec.description}
                  </p>
                </div>

                {/* Attachments */}
                {rec.attachments?.length > 0 && (
                  <div>
                    <p className="text-xs text-gray-400 mb-2">Attachments</p>
                    <div className="flex flex-wrap gap-2">
                      {rec.attachments.map((a) => (
                        <a
                          key={a._id}
                          href={a.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center space-x-1 text-xs text-blue-600 hover:text-blue-800 bg-blue-50 px-3 py-1 rounded-full border border-blue-200"
                        >
                          <ExternalLink className="h-3 w-3" />
                          <span>{a.originalName}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* Resolution inputs (only when actionable) */}
                {nextStatuses.length > 0 && (
                  <div className="space-y-3 pt-2 border-t border-gray-100">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Resolution
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">
                          Approved Amount ({rec.currency})
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={inputs.approvedAmount}
                          onChange={(e) =>
                            setResolutionInputs((prev) => ({
                              ...prev,
                              [rec._id]: { ...inputs, approvedAmount: e.target.value },
                            }))
                          }
                          placeholder="0.00"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Note</label>
                        <input
                          type="text"
                          value={inputs.resolutionNote}
                          onChange={(e) =>
                            setResolutionInputs((prev) => ({
                              ...prev,
                              [rec._id]: { ...inputs, resolutionNote: e.target.value },
                            }))
                          }
                          placeholder="Optional note"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {nextStatuses.map((s) => (
                        <button
                          key={s}
                          type="button"
                          disabled={updating === rec._id}
                          onClick={() => handleStatusUpdate(rec._id, s)}
                          className={`inline-flex items-center space-x-1.5 px-4 py-1.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 ${
                            s === "rejected"
                              ? "bg-red-50 text-red-700 border border-red-200 hover:bg-red-100"
                              : s === "approved" || s === "settled"
                              ? "bg-green-600 text-white hover:bg-green-700"
                              : "bg-blue-600 text-white hover:bg-blue-700"
                          }`}
                        >
                          {updating === rec._id && (
                            <Loader className="h-3.5 w-3.5 animate-spin" />
                          )}
                          <span>Mark as {STATUS_LABELS[s]}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Resolution result */}
                {rec.approvedAmount != null && (
                  <div className="flex items-center space-x-2 text-sm text-green-700 bg-green-50 px-3 py-2 rounded-lg">
                    <span>Approved amount:</span>
                    <span className="font-semibold">
                      {rec.currency} {rec.approvedAmount.toLocaleString()}
                    </span>
                  </div>
                )}
                {rec.resolutionNote && (
                  <p className="text-sm text-gray-600 italic">"{rec.resolutionNote}"</p>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ReclamationSection;
